import { GameState } from "../../../../core/protocol/types";

/**
 * Interface for game mode implementations
 * This is framework-agnostic and can be used in React, vanilla JS, or any other context
 */
export interface GameMode {
  /**
   * Handle game start/initialization for this mode
   */
  handleGameStart?(game: any): void;
  
  /**
   * Handle player death/respawn logic for this mode
   */
  handleDeath?(game: any, gameState: GameState): void;
  
  /**
   * Handle syncpoint changes for this mode
   */
  handleSyncpointChange?(game: any, syncpoint: string | undefined, gameState: GameState): void;
  
  /**
   * Check if player can respawn in this mode
   */
  canRespawn?(game: any, gameState: GameState): boolean;
  
  /**
   * Get mode-specific UI component props (if any)
   */
  getUIProps?(): Record<string, any>;
}

/**
 * Factory function to get the appropriate mode
 */
export function getGameMode(gameMode: string | undefined): GameMode {
  switch (gameMode) {
    case "survivor":
      return new SurvivorMode();
    case "standard":
    default:
      return new StandardMode();
  }
}

/**
 * Standard/FFA mode
 * In standard mode, players can respawn individually when they die
 */
class StandardMode implements GameMode {
  handleDeath(game: any, gameState: GameState): void {
    // In standard mode, death handling is done in DeadWaitState
    // when player presses a key. No special logic needed here.
  }

  canRespawn(game: any, gameState: GameState): boolean {
    // In standard mode, players can always respawn by pressing a key
    // This is handled in DeadWaitState.update()
    return true;
  }
}

/**
 * Survivor mode
 * In survivor mode, players must wait for round restarts coordinated via syncpoints
 */
class SurvivorMode implements GameMode {
  handleGameStart(game: any): void {
    // In survivor mode, game start is handled by syncpoint transitions
    // Don't restart here - that's handled by handleSyncpointChange() and DeadWaitState
    // This method is just for initial setup if needed
  }

  handleDeath(game: any, gameState: GameState): void {
    // In survivor mode, players wait for syncpoint transitions
    // This is handled in DeadWaitState, but we can add additional logic here if needed
  }

  handleSyncpointChange(game: any, syncpoint: string | undefined, gameState: GameState): void {
    // Syncpoint handling is now done entirely by DeadWaitState.update()
    // This matches the original C++ behavior where Player_dead_wait::check_first_frag()
    // handles all syncpoint transitions internally without external intervention
    // The React component should NOT manipulate game states - that's the state machine's job
  }

  canRespawn(game: any, gameState: GameState): boolean {
    // In survivor mode, respawn is controlled by syncpoint
    // Players cannot respawn individually - must wait for round restart
    const syncpoint = gameState.roundSyncState;
    return syncpoint === "playing";
  }
}

